import { useState } from "react";
import { Star, Trash2, User } from "lucide-react";
import { useReviews } from "@/hooks/useReviews";
import { useAuth } from "@/hooks/useAuth";

const StarRating = ({
  rating,
  onRate,
  interactive = false,
  size = "w-4 h-4",
}: {
  rating: number;
  onRate?: (n: number) => void;
  interactive?: boolean;
  size?: string;
}) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          onClick={() => onRate?.(n)}
          onMouseEnter={() => interactive && setHover(n)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
        >
          <Star
            className={`${size} ${
              n <= (hover || rating)
                ? "text-amber-400 fill-amber-400"
                : "text-muted-foreground/30"
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewSection = ({ toolSlug }: { toolSlug: string }) => {
  const { user } = useAuth();
  const {
    reviews,
    loading,
    submitting,
    submitReview,
    deleteReview,
    averageRating,
    userReview,
  } = useReviews(toolSlug);

  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(userReview?.rating || 0);
  const [title, setTitle] = useState(userReview?.title || "");
  const [content, setContent] = useState(userReview?.content || "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) return;
    const ok = await submitReview(rating, title.trim(), content.trim());
    if (ok) {
      setShowForm(false);
      setRating(0);
      setTitle("");
      setContent("");
    }
  };

  const handleEdit = () => {
    if (userReview) {
      setRating(userReview.rating);
      setTitle(userReview.title);
      setContent(userReview.content || "");
    }
    setShowForm(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-mono font-bold text-foreground text-sm flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-400" /> Reviews
          {reviews.length > 0 && (
            <span className="text-muted-foreground font-normal">
              ({averageRating.toFixed(1)} avg · {reviews.length})
            </span>
          )}
        </h3>
        {user && !showForm && (
          <button
            onClick={handleEdit}
            className="text-xs font-mono px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            {userReview ? "Edit Review" : "Write Review"}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 rounded-lg bg-secondary/50 border border-border space-y-3">
          <div>
            <label className="text-xs font-mono text-muted-foreground block mb-1">Rating *</label>
            <StarRating rating={rating} onRate={setRating} interactive size="w-6 h-6" />
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground block mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
              placeholder="Summarize your experience"
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground block mb-1">Details</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Share your detailed experience..."
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting ? "Submitting..." : userReview ? "Update" : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-lg bg-secondary text-muted-foreground font-mono text-sm hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-xs font-mono text-muted-foreground">Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <p className="text-xs font-mono text-muted-foreground">No reviews yet. Be the first to review!</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="p-3 rounded-lg bg-secondary/30 border border-border">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-xs font-mono text-foreground">
                    {review.profile?.display_name || "Anonymous"}
                  </span>
                  <StarRating rating={review.rating} size="w-3 h-3" />
                </div>
                {user && review.user_id === user.id && (
                  <button
                    onClick={() => deleteReview(review.id)}
                    className="text-muted-foreground hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <h4 className="text-sm font-mono font-semibold text-foreground mb-1">{review.title}</h4>
              {review.content && (
                <p className="text-xs text-muted-foreground">{review.content}</p>
              )}
              <p className="text-[10px] text-muted-foreground/60 mt-2 font-mono">
                {new Date(review.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
